<script>
import { mapStores } from 'pinia'
import { useCartStore } from '@/cart/stores/cart'
import { parseLocalDate } from '@/utils'
import api from '@/cartapi.js'

export default {
  name: 'OrderConfirmedView',

  props: {
    orderToken: { type: String, default: null },
  },

  data() {
    return {
      loading: true,
      error: null,
      order: null,
    }
  },

  computed: {
    ...mapStores(useCartStore),

    formattedDates() {
      if (!this.order || !this.order.dates) return ''
      const dates = this.order.dates.map((d) => parseLocalDate(d)).sort((a, b) => a - b)
      if (dates.length === 0) return ''

      if (dates.length === 1) {
        return dates[0].toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      }

      // Check if dates are contiguous (each date is exactly 1 day after the previous)
      const isContiguous = dates.every((date, index) => {
        if (index === 0) return true
        const prevDate = dates[index - 1]
        const diffDays = (date - prevDate) / (1000 * 60 * 60 * 24)
        return diffDays === 1
      })

      if (isContiguous) {
        // Show as range: "Jun 12 - Jun 14, 2025"
        const start = dates[0]
        const end = dates[dates.length - 1]
        const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const endStr = end.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
        return `${startStr} - ${endStr}`
      }

      // Non-contiguous: list individual dates
      const formatOpts = { weekday: 'short', month: 'short', day: 'numeric' }
      if (dates.length <= 5) {
        return dates.map((d) => d.toLocaleDateString('en-US', formatOpts)).join(', ')
      }

      // More than 5 non-contiguous dates: show first 3 + count
      const first3 = dates.slice(0, 3).map((d) => d.toLocaleDateString('en-US', formatOpts))
      return `${first3.join(', ')} (+${dates.length - 3} more)`
    },

    refId() {
      if (this.order?.token) {
        return this.order.token.toUpperCase()
      }
      return 'PENDING'
    },
  },

  async mounted() {
    await this.fetchOrder()
  },

  methods: {
    async fetchOrder() {
      this.loading = true
      this.error = null

      try {
        // Fetch order details
        const order = await api.orders.get(this.orderToken)
        this.order = order
      } catch (err) {
        console.error('Failed to fetch order', err)
        this.error = 'Could not load order details.'
      } finally {
        this.loading = false
      }
    },

    goHome() {
      const eventSlug = this.order?.event_slug
      if (eventSlug) {
        this.$router.push({ name: 'EventLocations', params: { eventSlug } })
      } else {
        window.location.href = '/'
      }
    },
  },
}
</script>

<template>
  <section class="section">
    <div class="container is-max-desktop">
      <!-- Loading State -->
      <div v-if="loading" class="has-text-centered py-6">
        <span class="icon is-large">
          <i class="mdi mdi-loading mdi-spin mdi-48px"></i>
        </span>
        <p class="mt-4">Loading confirmation...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="notification is-danger">
        <button class="delete"></button>
        {{ error }}
      </div>

      <!-- Success Content -->
      <div v-else class="content">
        <!-- Header -->
        <div class="has-text-centered mb-6">
          <span class="icon has-text-success is-large" style="font-size: 4rem; height: 5rem">
            <i class="mdi mdi-check-circle"></i>
          </span>
          <h1 class="title is-2 mt-4">Success!</h1>
          <p class="subtitle is-4">
            {{ order.quantity > 1 ? `${order.quantity} lockers reserved.` : 'Your locker is reserved.' }}
          </p>
          <p v-if="order.checkout_confirmation" class="has-text-grey mt-3">
            {{ order.checkout_confirmation }}
          </p>
        </div>

        <div class="columns is-centered">
          <div class="column is-8">
            <!-- Order Summary Card -->
            <div class="box">
              <h2 class="title is-5 mb-4">Order Summary</h2>

              <div class="columns is-mobile is-multiline">
                <div class="column is-6">
                  <p class="heading">Event</p>
                  <p class="title is-6">{{ order.event_name || 'Goatchella' }}</p>
                </div>
                <div class="column is-6">
                  <p class="heading">Locker Type</p>
                  <p class="title is-6">{{ order.product_name || 'Locker' }}</p>
                </div>
                <div class="column is-6">
                  <p class="heading">Dates</p>
                  <p class="title is-6">{{ formattedDates }}</p>
                </div>
                <div class="column is-6">
                  <p class="heading">Quantity</p>
                  <p class="title is-6">{{ order.quantity || 1 }}</p>
                </div>
              </div>

              <hr />

              <!-- Price Breakdown -->
              <div class="price-breakdown">
                <div class="level is-mobile mb-2">
                  <div class="level-left">
                    <span class="has-text-grey">Subtotal</span>
                  </div>
                  <div class="level-right">
                    <span
                      >${{ (order.subtotal || order.total - (order.tax || 0)).toFixed(2) }}</span
                    >
                  </div>
                </div>
                <div v-if="order.tax && order.tax > 0" class="level is-mobile mb-2">
                  <div class="level-left">
                    <span class="has-text-grey">Tax</span>
                  </div>
                  <div class="level-right">
                    <span>${{ order.tax.toFixed(2) }}</span>
                  </div>
                </div>
                <hr class="my-2" />
                <div class="level is-mobile">
                  <div class="level-left">
                    <strong>Total Paid</strong>
                  </div>
                  <div class="level-right">
                    <strong class="is-size-5">${{ order.total.toFixed(2) }}</strong>
                  </div>
                </div>
              </div>

              <!-- Policy Reminder -->
              <p class="is-size-7 has-text-grey has-text-centered mt-3 mb-3">
                All sales are final. See Terms of Service for details.
              </p>

              <hr />

              <!-- Reference ID -->
              <div class="has-text-centered py-4 has-background-light" style="border-radius: 8px">
                <p class="heading mb-2">Order Reference</p>
                <p class="title is-3 is-family-monospace has-text-primary">
                  {{ refId }}
                </p>
                <p class="is-size-7 mt-2">Present this code at check-in</p>
              </div>
            </div>

            <!-- Next Steps -->
            <div class="box has-background-info-light">
              <h3 class="title is-5">What's Next?</h3>
              <div class="content">
                <ul class="mt-0">
                  <li>
                    <strong>Check your email:</strong> A confirmation has been sent to
                    <span class="has-text-weight-bold">{{ order.customerEmail }}</span
                    >.
                  </li>
                  <li v-if="order.smsConsent && order.customerPhone">
                    <strong>Text updates:</strong> We'll text your locker number and
                    combo to
                    <span class="has-text-weight-bold">{{ order.customerPhone }}</span>
                    when assigned.
                  </li>
                  <li>
                    <strong>Claim your locker:</strong> Bring your Reference ID and a valid ID to
                    the locker booth at
                    <span class="tag is-info is-light">{{
                      order.location_display_name || order.location || 'Locker Area'
                    }}</span
                    >.
                  </li>
                  <li v-if="order.policy_settings?.rental_end_rule">
                    <strong>Rental Period:</strong> {{ order.policy_settings.rental_end_rule }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Actions -->
            <div class="has-text-centered mt-6">
              <button class="button is-text" @click="goHome">Return to Event Page</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.box {
  border-radius: 12px;
  box-shadow:
    0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1),
    0 0px 0 1px rgba(10, 10, 10, 0.02);
}
</style>
